import { calculateGameplanAffordability, hasPaymentMethod, isFrictionlessSignupUser } from '@frontend/api-client';
import type { ComplexityAgreement } from '@frontend/api-types';
import { pluralize } from '@frontend/duck-tape';
import { useMemo, useRef, useState, useVisibilityController } from '@frontend/react';
import { Button, Divider, Icon, Text, Tooltip, XStack, YStack, useHotkeys } from '@frontend/web-react';
import { BuyCreditsModal } from '@frontend/web/components/DomainSpecific/Payment/BuyCreditsModal/BuyCreditsModal';
import { PaywallModal } from '@frontend/web/components/DomainSpecific/Paywall';
import { usePricingTerminology } from '@frontend/web/context';
import {
  useAcceptComplexityPricingMutation,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveCreditsQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { match } from 'ts-pattern';

type GameplanCardProps = {
  agreement: ComplexityAgreement;
  onAcceptedOrDeclined?: () => Promise<void> | void;
  taskId: string;
};

export const GameplanCard = ({ agreement, onAcceptedOrDeclined, taskId }: GameplanCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const { id, status, title } = agreement;
  const { data: credits, refetch: refetchCredits } = useRetrieveCreditsQuery();
  const { data: client } = useRetrieveClientQuery();
  const { data: clientStatus, refetch: refetchClientStatus } = useRetrieveClientStatusQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const [acceptComplexityPricing, { isLoading }] = useAcceptComplexityPricingMutation();
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false);
  const [isPaywallModalOpen, { close: hidePaywallModal, open: showPaywallModal }] = useVisibilityController();

  const availableCredits = credits?.availableCredits ?? 0;
  const affordability = calculateGameplanAffordability(agreement.creditCost, availableCredits);

  const handleAccept = async (isAccepted: boolean) => {
    await handleMutation({
      mutation: () => acceptComplexityPricing({ complexityPricingAgreement: id, isAccepted, taskId }).unwrap(),
      onSuccess: async () => {
        if (isAccepted) {
          await refetchCredits();
        }
        await onAcceptedOrDeclined?.();
      },
      showErrorToast: true,
      showSuccessToast: false,
    });
  };

  const onPaywallSuccess = async () => {
    await Promise.allSettled([refetchCredits(), refetchClientStatus()]);
    hidePaywallModal();
  };

  const canAccept = status === 'pending' && !isLoading && affordability.state !== 'insufficientCredits';
  const canDecline = status === 'pending' && !isLoading;
  const needsPaywall = !hasPaymentMethod(clientStatus) && client?.isHouseholdOwner && status === 'pending';

  useHotkeys([
    [
      'Escape',
      () => {
        if (!cardRef.current?.contains(document.activeElement)) return;
        if (canDecline) {
          handleAccept(false);
        }
      },
    ],
    [
      'Enter',
      () => {
        if (!cardRef.current?.contains(document.activeElement)) return;
        if (needsPaywall) {
          showPaywallModal();
        } else if (canAccept) {
          handleAccept(true);
        }
      },
    ],
  ]);

  const footer = useMemo(() => {
    if (status === 'pending' && !hasPaymentMethod(clientStatus) && client?.isHouseholdOwner) {
      return (
        <>
          <Divider />
          <XStack alignItems="center" gap="md">
            <YStack flexGrow={1}>
              <Text className="text-neutral-950" type="h5">
                Start gameplan?
              </Text>
              <Text className="text-neutral-950" type="p-sm">
                This gameplan will be routed to a human specialist to complete.
              </Text>
            </YStack>
            <XStack alignItems="center" gap="sm">
              <Button
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="h-[40px] gap-sm"
                disabled={isLoading}
                isLoading={isLoading}
                label="Decline"
                onClick={() => handleAccept(false)}
                rightSection={
                  <XStack className="bg-cream-200 rounded-sm px-xs">
                    <Text className="text-neutral-950" type="p-sm">
                      esc
                    </Text>
                  </XStack>
                }
                variant="secondary"
              />
              <Button
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="h-[40px] gap-sm border-2 border-[#77DFC4] bg-neutral-950 hover:bg-neutral-950 active:opacity-80 focus:opacity-90"
                disabled={isLoading}
                isLoading={isLoading}
                label={`Get ${pricingTerminology.unit_term_plural} and submit`}
                onClick={showPaywallModal}
                rightSection={
                  <XStack className="bg-cream-200 rounded-sm px-xs">
                    <Text className="text-neutral-950" type="p-sm">
                      ⏎
                    </Text>
                  </XStack>
                }
                variant="primary"
              />
            </XStack>
          </XStack>
        </>
      );
    } else if (status === 'pending') {
      return match(affordability)
        .with({ state: 'insufficientCredits' }, (aff) => (
          <>
            <XStack
              alignItems="center"
              bg="red50"
              borderColor="red200"
              borderWidth={1}
              gap="xs"
              justifyContent="between"
              p="sm"
              radius="lg"
            >
              <XStack alignItems="center" gap="sm">
                <Icon color="danger" name="IconInfoCircleFilled" size={24} />
                <Text color="danger" type="p-md">
                  {showCredits
                    ? `You're short ${aff.creditShortfall} ${pluralize(pricingTerminology.unit_term, aff.creditShortfall)}.`
                    : pricingTerminology.insufficient_label}
                </Text>
              </XStack>
              <Button
                // eslint-disable-next-line tailwindcss/no-arbitrary-value
                className="h-[32px] px-md"
                label={pricingTerminology.add_button}
                onClick={() => setIsBuyCreditsOpen(true)}
                size="sm"
                variant="destructive"
              />
            </XStack>
            <Divider />
            <XStack alignItems="center" gap="md">
              <YStack flexGrow={1}>
                <Text className="text-neutral-950" type="h5">
                  Start gameplan?
                </Text>
                <Text className="text-neutral-950" type="p-sm">
                  This gameplan will be routed to a human specialist to complete.
                </Text>
              </YStack>
              <XStack alignItems="center" gap="sm">
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-[40px] gap-sm"
                  disabled={isLoading}
                  isLoading={isLoading}
                  label="Decline"
                  onClick={() => handleAccept(false)}
                  rightSection={
                    <XStack className="bg-cream-200 rounded-sm px-xs">
                      <Text className="text-neutral-950" type="p-sm">
                        esc
                      </Text>
                    </XStack>
                  }
                  variant="secondary"
                />
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-[40px] gap-sm"
                  disabled={true}
                  isLoading={isLoading}
                  label={
                    showCredits
                      ? `Approve for ${aff.creditCost} ${pluralize(pricingTerminology.unit_term, aff.creditCost)}`
                      : 'Approve'
                  }
                  onClick={() => handleAccept(true)}
                  rightSection={
                    <XStack className="bg-cream-200 rounded-sm px-xs">
                      <Text className="text-neutral-950" type="p-sm">
                        ⏎
                      </Text>
                    </XStack>
                  }
                  variant="primary"
                />
              </XStack>
            </XStack>
          </>
        ))
        .with({ state: 'free' }, { state: 'hiddenPrice' }, () => (
          <>
            <Divider />
            <XStack alignItems="center" gap="md">
              <YStack flexGrow={1}>
                <Text className="text-neutral-950" type="h5">
                  Start gameplan?
                </Text>
                <Text className="text-neutral-950" type="p-sm">
                  This gameplan will be routed to a human specialist to complete.
                </Text>
              </YStack>
              <XStack alignItems="center" gap="sm">
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-[40px] gap-sm"
                  disabled={isLoading}
                  isLoading={isLoading}
                  label="Decline"
                  onClick={() => handleAccept(false)}
                  rightSection={
                    <XStack className="bg-cream-200 rounded-sm px-xs">
                      <Text className="text-neutral-950" type="p-sm">
                        esc
                      </Text>
                    </XStack>
                  }
                  variant="secondary"
                />
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-[40px] gap-sm"
                  disabled={isLoading}
                  isLoading={isLoading}
                  label="Start"
                  onClick={() => handleAccept(true)}
                  rightSection={
                    <XStack className="bg-cream-200 rounded-sm px-xs">
                      <Text className="text-neutral-950" type="p-sm">
                        ⏎
                      </Text>
                    </XStack>
                  }
                  variant="primary"
                />
              </XStack>
            </XStack>
          </>
        ))
        .with({ state: 'affordable' }, (aff) => (
          <>
            <Divider />
            <XStack alignItems="center" gap="md">
              <YStack flexGrow={1}>
                <Text className="text-neutral-950" type="h5">
                  Start gameplan?
                </Text>
                <Text className="text-neutral-950" type="p-sm">
                  This gameplan will be routed to a human specialist to complete.
                </Text>
              </YStack>
              <XStack alignItems="center" gap="sm">
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-[40px] gap-sm"
                  disabled={isLoading}
                  isLoading={isLoading}
                  label="Decline"
                  onClick={() => handleAccept(false)}
                  rightSection={
                    <XStack className="bg-cream-200 rounded-sm px-xs">
                      <Text className="text-neutral-950" type="p-sm">
                        esc
                      </Text>
                    </XStack>
                  }
                  variant="secondary"
                />
                <Button
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  className="h-[40px] gap-sm"
                  disabled={isLoading}
                  isLoading={isLoading}
                  label={
                    showCredits
                      ? `Start for ${aff.creditCost} ${pluralize(pricingTerminology.unit_term, aff.creditCost)}`
                      : 'Start'
                  }
                  onClick={() => handleAccept(true)}
                  rightSection={
                    <XStack className="bg-cream-200 rounded-sm px-xs">
                      <Text className="text-neutral-950" type="p-sm">
                        ⏎
                      </Text>
                    </XStack>
                  }
                  variant="primary"
                />
              </XStack>
            </XStack>
          </>
        ))
        .exhaustive();
    } else {
      return null;
    }
  }, [clientStatus, client, credits, agreement, affordability, isLoading]);

  return (
    <>
      <YStack
        bg="cream50"
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="p-[3px] flex-1"
        radius="lg"
        ref={cardRef}
      >
        <XStack alignItems="center" justifyContent="between" p="sm">
          <XStack alignItems="center" gap="xs">
            <Icon iconClassName="text-neutral-950" name="IconFileDescription" size={20} />
            <Text className="text-neutral-950" type="p-md">
              Gameplan
            </Text>
          </XStack>
          {status === 'accepted' ? (
            <XStack alignItems="center" bg="cream50" gap="xs" px="sm" py="xs" radius="md">
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <Text className="text-neutral-900 font-[500]" color="success" type="caption">
                Accepted
              </Text>
            </XStack>
          ) : null}
          {status === 'rejected' ? (
            <XStack alignItems="center" bg="neutral50" gap="xs" px="sm" py="xs" radius="md">
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
              <Text className="text-neutral-900 font-[500]" type="caption">
                Declined
              </Text>
            </XStack>
          ) : null}
        </XStack>
        <YStack bg="surfaceSecondary" className="shadow-one" gap="md" p="md" radius="lg">
          <Text color="primary" type="p-md">
            {title}
          </Text>
          <YStack>
            {showCredits && affordability.state !== 'hiddenPrice' ? (
              <Text color="tertiary" type="caption">
                Cost:
              </Text>
            ) : null}
            <XStack alignItems="center" gap="xs">
              <Text className="text-neutral-950" type="p-sm">
                {showCredits && affordability.state !== 'hiddenPrice'
                  ? `${affordability.creditCost} ${pluralize(pricingTerminology.unit_term, affordability.creditCost)} • `
                  : ''}
                Task size: {agreement.complexityBucket}
              </Text>
              <Tooltip
                arrowOffset={10}
                arrowSize={4}
                className="rounded-lg"
                label={
                  <YStack className="max-w-sm p-sm rounded-lg bg-neutral-950">
                    <Text className="whitespace-normal break-words" color="inverse" type="p-sm">
                      Each task is sized based on thousands {"we've"} handled before - balanced across effort,
                      complexity, and human time. If yours requires significantly more than the usual scope, {"we'll"}{' '}
                      check in and suggest additional gameplans to keep work flowing seamlessly.
                    </Text>
                  </YStack>
                }
                position="top-start"
                withArrow
              >
                <Icon color="tertiary" name="IconInfoCircle" size={16} />
              </Tooltip>
            </XStack>
          </YStack>
          {footer}
        </YStack>
      </YStack>
      <PaywallModal
        isFrictionlessUser={isFrictionlessSignupUser(client)}
        isOpen={isPaywallModalOpen}
        minimumCredits={agreement.creditCost ?? undefined}
        onClose={hidePaywallModal}
        onSuccess={onPaywallSuccess}
      />
      <BuyCreditsModal
        closeModal={() => setIsBuyCreditsOpen(false)}
        isOpen={isBuyCreditsOpen}
        minimumCredits={affordability.creditShortfall}
        onCreditsPurchased={async () => {
          await refetchCredits();
          setIsBuyCreditsOpen(false);
        }}
        stepTitleLookupOverrides={{
          selectPackage: () => `Get ${pricingTerminology.unit_term_plural} to start your task`,
        }}
      />
    </>
  );
};
